import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { SimpleMarkdown } from "../../../shared/components/SimpleMarkdown.tsx";
import {
  useEvAgentContextQuery,
  useEvProjectCallPrepStatusQuery,
} from "../hooks/use-ev-workstation-queries.ts";
import { toCallPrepVisualStatus } from "../lib/call-prep-status.ts";
import type {
  EvBriefPayload,
  EvOperatingSummaryArtifact,
  EvPriorityActionInput,
  EvPriorityActionType,
  EvWorkstationProject,
} from "../types/index.ts";
import { CallPrepStatusBadge, CallPrepStructuredView } from "./CallPrepStructuredView.tsx";

interface EvDailyBriefPanelProps {
  brief: EvBriefPayload | null;
  loading: boolean;
  onOpenProject: (
    projectId: string,
    options?: { detailView?: "full" | "call_prep" | "operating_summary" }
  ) => void | Promise<void>;
  onApplyPriorityAction: (projectId: string, input: EvPriorityActionInput) => Promise<void>;
  priorityActionPendingKey: string | null;
}

function renderProjectLabel(projectName: string, twitterHandle: string): string {
  if (projectName.toLowerCase() === twitterHandle.toLowerCase()) {
    return `@${twitterHandle}`;
  }
  return `${projectName} (@${twitterHandle})`;
}

function buildOperatingSummaryMarkdown(
  payload: EvOperatingSummaryArtifact | null | undefined
): string {
  if (!payload) {
    return "No generated summary yet.";
  }

  return [
    payload.summary,
    payload.whyItMatters ? `### Why It Matters\n${payload.whyItMatters}` : null,
    payload.relationshipStatus ? `### Relationship Status\n${payload.relationshipStatus}` : null,
    payload.immediateNextAction
      ? `### Immediate Next Action\n${payload.immediateNextAction}`
      : null,
    payload.keyPoints?.length
      ? `### Key points\n${payload.keyPoints.map((point) => `- ${point}`).join("\n")}`
      : null,
    payload.keyBlockers?.length
      ? `### Blockers\n${payload.keyBlockers.map((point) => `- ${point}`).join("\n")}`
      : null,
  ]
    .filter((section): section is string => Boolean(section))
    .join("\n\n");
}

function formatPriorityStateLabel(state: EvBriefPayload["priorities"][number]["state"]): string {
  switch (state) {
    case "new":
      return "New";
    case "carried":
      return "Carried";
    case "escalated":
      return "Escalated";
    case "blocked":
      return "Blocked";
    case "stale_review_required":
      return "Needs review";
  }
}

function formatPriorityResolutionLabel(
  resolution: EvBriefPayload["priorities"][number]["requiredResolution"]
): string {
  switch (resolution) {
    case "act_now":
      return "Act now";
    case "update_workflow":
      return "Update workflow";
    case "add_note":
      return "Add reason";
    case "snooze":
      return "Snooze";
    case "resolve":
      return "Resolve";
  }
}

function formatPriorityActionLabel(action: EvPriorityActionType): string {
  switch (action) {
    case "resolve":
      return "Resolve";
    case "snooze":
      return "Snooze";
    case "blocked":
      return "Blocked";
  }
}

function formatPriorityReasonLabel(action: EvPriorityActionType): string {
  switch (action) {
    case "resolve":
      return "Why is this resolved now?";
    case "snooze":
      return "Why should this be snoozed?";
    case "blocked":
      return "What is blocking this priority?";
  }
}

function normalizePriorityReason(value: string): string {
  return value.replace(/\s+/g, " ").trim();
}

function getDefaultSnoozeDate(dueAt: string | null): string {
  const candidate = dueAt ? new Date(dueAt) : new Date();
  if (Number.isNaN(candidate.getTime())) {
    const fallback = new Date();
    fallback.setDate(fallback.getDate() + 1);
    return fallback.toISOString().slice(0, 10);
  }
  candidate.setDate(candidate.getDate() + 1);
  return candidate.toISOString().slice(0, 10);
}

function formatBriefDateTime(value: string | null): string {
  if (!value) {
    return "—";
  }

  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
    const parsedDate = new Date(`${value}T00:00:00.000Z`);
    if (!Number.isNaN(parsedDate.getTime())) {
      return parsedDate.toLocaleDateString([], {
        month: "short",
        day: "numeric",
      });
    }
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return "—";
  }

  return date.toLocaleString([], {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

function PriorityActionModal({
  item,
  action,
  pending,
  onClose,
  onSubmit,
}: {
  item: EvBriefPayload["priorities"][number];
  action: EvPriorityActionType;
  pending: boolean;
  onClose: () => void;
  onSubmit: (input: EvPriorityActionInput) => Promise<void>;
}) {
  const [reason, setReason] = useState("");
  const [snoozedUntil, setSnoozedUntil] = useState(getDefaultSnoozeDate(item.dueAt));
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setReason("");
    setSnoozedUntil(getDefaultSnoozeDate(item.dueAt));
    setError(null);
  }, [item.dueAt]);

  useEffect(() => {
    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape" && !pending) {
        onClose();
      }
    }

    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [onClose, pending]);

  async function handleSubmit() {
    const normalizedReason = normalizePriorityReason(reason);
    if (!normalizedReason) {
      setError("Give a specific reason so EV and the agent know why this changed.");
      return;
    }

    if (action === "snooze" && !snoozedUntil) {
      setError("Choose a return date for the snoozed priority.");
      return;
    }

    setError(null);

    try {
      await onSubmit({
        priorityKey: item.priorityKey,
        action,
        reason: normalizedReason,
        snoozedUntil: action === "snooze" ? snoozedUntil : null,
      });
      onClose();
    } catch (submitError) {
      setError(
        submitError instanceof Error ? submitError.message : "Could not apply the priority action."
      );
    }
  }

  if (typeof document === "undefined") {
    return null;
  }

  return createPortal(
    <div
      className="modal-overlay"
      onClick={() => {
        if (!pending) onClose();
      }}
      onKeyDown={(event) => {
        if (
          (event.key === "Enter" || event.key === " ") &&
          event.target === event.currentTarget &&
          !pending
        ) {
          event.preventDefault();
          onClose();
        }
      }}
      role="dialog"
      aria-modal="true"
      aria-labelledby="priority-action-modal-title"
      tabIndex={-1}
    >
      <div
        className="modal ev-priority-action-modal"
        onClick={(event) => event.stopPropagation()}
        onKeyDown={(event) => event.stopPropagation()}
        role="document"
      >
        <div className="modal-header">
          <div>
            <div className="ev-priority-action-modal__eyebrow">
              {formatPriorityActionLabel(action)} priority
            </div>
            <h2 id="priority-action-modal-title">
              {renderProjectLabel(item.projectName, item.twitterHandle)}
            </h2>
            <div className="ev-priority-action-modal__subhead">{item.headline}</div>
          </div>
          <button
            type="button"
            className="modal-close"
            onClick={onClose}
            aria-label="Close modal"
            disabled={pending}
          >
            &times;
          </button>
        </div>

        <div className="modal-body ev-priority-action-modal__body">
          <div className="ev-priority-action-modal__summary">
            <div className="ev-priority-action-modal__summary-item">
              <span className="ev-brief-panel__mini-label">Why today</span>
              <span>{item.whyToday}</span>
            </div>
            <div className="ev-priority-action-modal__summary-item">
              <span className="ev-brief-panel__mini-label">Operator instruction</span>
              <span>{item.operatorInstruction}</span>
            </div>
            <div className="ev-priority-action-modal__summary-item">
              <span className="ev-brief-panel__mini-label">Resolution hint</span>
              <span>{item.resolutionHint}</span>
            </div>
          </div>

          {action === "snooze" ? (
            <label className="ev-priority-action-modal__field">
              <span className="ev-brief-panel__mini-label">Return on</span>
              <input
                className="ev-priority-action-modal__input"
                type="date"
                value={snoozedUntil}
                onChange={(event) => setSnoozedUntil(event.target.value)}
                disabled={pending}
              />
            </label>
          ) : null}

          <label className="ev-priority-action-modal__field">
            <span className="ev-brief-panel__mini-label">{formatPriorityReasonLabel(action)}</span>
            <textarea
              className="ev-priority-action-modal__textarea"
              rows={5}
              value={reason}
              onChange={(event) => setReason(event.target.value)}
              placeholder={
                action === "resolve"
                  ? "Explain what changed, what was done, and why this should stop showing up."
                  : action === "snooze"
                    ? "Explain why this can wait and what should happen before it comes back."
                    : "Explain the blocker, the owner, and what needs to happen before this can move."
              }
              disabled={pending}
            />
          </label>

          {error ? <div className="ev-priority-action-modal__error">{error}</div> : null}

          <div className="ev-priority-action-modal__footer">
            <button
              type="button"
              className="btn btn-small btn-secondary"
              onClick={onClose}
              disabled={pending}
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn btn-small"
              onClick={() => void handleSubmit()}
              disabled={pending}
            >
              {pending ? "Saving..." : formatPriorityActionLabel(action)}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}

function PriorityCard({
  item,
  expanded,
  onToggleExpanded,
  onOpen,
  onSelectAction,
  actionPendingKey,
}: {
  item: EvBriefPayload["priorities"][number];
  expanded: boolean;
  onToggleExpanded: (key: string) => void;
  onOpen: () => void | Promise<void>;
  onSelectAction: (
    item: EvBriefPayload["priorities"][number],
    action: EvPriorityActionType
  ) => void;
  actionPendingKey: string | null;
}) {
  const itemKey = `priority-${item.priorityKey}`;
  const isPending = actionPendingKey === item.priorityKey;
  const agentContextQuery = useEvAgentContextQuery(item.projectId, expanded);
  const operatingSummary = agentContextQuery.data?.artifacts.operatingSummary?.payload;
  const expandedMarkdown = buildOperatingSummaryMarkdown(operatingSummary);
  const summaryText = item.reason?.trim() || item.whyToday?.trim() || null;

  return (
    <div className="ev-brief-panel__item ev-brief-panel__item--priority">
      <button type="button" className="ev-brief-panel__item-hitbox" onClick={() => void onOpen()}>
        <div className="ev-brief-panel__item-head ev-brief-panel__item-head--stacked">
          <div className="ev-brief-panel__item-title-wrap">
            <span>{renderProjectLabel(item.projectName, item.twitterHandle)}</span>
            <span
              className={`ev-brief-panel__priority-state ev-brief-panel__priority-state--${item.state}`}
            >
              {formatPriorityStateLabel(item.state)}
            </span>
          </div>
          <div className="ev-brief-panel__item-badges">
            <span className="mono">
              {item.dueAt ? formatBriefDateTime(item.dueAt) : item.category}
            </span>
            <span className="mono">{formatPriorityResolutionLabel(item.requiredResolution)}</span>
            {item.carryoverDays > 0 ? (
              <span className="mono">{item.carryoverDays}d carry</span>
            ) : null}
          </div>
        </div>
        {summaryText ? <div className="ev-brief-panel__call-summary">{summaryText}</div> : null}
        <div className="ev-brief-panel__priority-guidance">{item.operatorInstruction}</div>
      </button>

      <div className="ev-brief-panel__item-actions ev-brief-panel__item-actions--priority">
        <button
          type="button"
          className="ev-brief-panel__action-button ev-brief-panel__action-button--open"
          onClick={() => void onOpen()}
        >
          Open project
        </button>
        <button
          type="button"
          className="ev-brief-panel__action-button ev-brief-panel__action-button--resolve"
          onClick={() => onSelectAction(item, "resolve")}
          disabled={isPending}
        >
          Resolve
        </button>
        <button
          type="button"
          className="ev-brief-panel__action-button ev-brief-panel__action-button--snooze"
          onClick={() => onSelectAction(item, "snooze")}
          disabled={isPending}
        >
          Snooze
        </button>
        <button
          type="button"
          className="ev-brief-panel__action-button ev-brief-panel__action-button--blocked"
          onClick={() => onSelectAction(item, "blocked")}
          disabled={isPending}
        >
          Blocked
        </button>
        <button
          type="button"
          className="ev-brief-panel__toggle"
          onClick={() => onToggleExpanded(itemKey)}
        >
          {expanded ? "Show less" : "View more"}
        </button>
      </div>

      {expanded ? (
        <div className="ev-brief-panel__item-expanded">
          {agentContextQuery.isLoading ? (
            <div className="ev-brief-panel__call-progress">Loading operating summary...</div>
          ) : (
            <SimpleMarkdown
              markdown={expandedMarkdown}
              className="markdown-block markdown-block--brief"
            />
          )}
        </div>
      ) : null}
    </div>
  );
}

function CallPrepCard({
  item,
  expanded,
  onToggleExpanded,
  onOpen,
}: {
  item: EvBriefPayload["calls"][number];
  expanded: boolean;
  onToggleExpanded: (key: string) => void;
  onOpen: () => void | Promise<void>;
}) {
  const itemKey = `call-${item.projectId}`;
  const liveStatusQuery = useEvProjectCallPrepStatusQuery(
    item.projectId,
    item.status === "generating"
  );
  const liveStatus = liveStatusQuery.data;
  const displayStatus = toCallPrepVisualStatus({
    callReadyStatus:
      item.status === "generating"
        ? "generating"
        : item.status === "needs_input"
          ? "needs_input"
          : item.status === "fresh"
            ? "ready"
            : "idle",
    artifactStatus:
      item.status === "failed"
        ? "failed"
        : item.status === "needs_input"
          ? "needs_input"
          : item.status === "fresh" || item.status === "stale"
            ? "completed"
            : null,
    artifactPayload: { summary: item.summary },
    artifactFreshnessState: item.freshnessState,
    liveStatus: liveStatus?.status,
    liveFreshnessState: liveStatus?.freshnessState ?? item.freshnessState,
  });
  const progressMessage =
    displayStatus === "generating"
      ? (liveStatus?.progressMessage ?? "Generating call prep...")
      : null;
  const errorMessage = displayStatus === "failed" ? liveStatus?.errorMessage : null;

  return (
    <div className="ev-brief-panel__item ev-brief-panel__item--call-prep">
      <button type="button" className="ev-brief-panel__item-hitbox" onClick={() => void onOpen()}>
        <div className="ev-brief-panel__item-head ev-brief-panel__item-head--stacked">
          <div className="ev-brief-panel__item-title-wrap">
            <span>{renderProjectLabel(item.projectName, item.twitterHandle)}</span>
            <CallPrepStatusBadge status={displayStatus} />
          </div>
          <div className="ev-brief-panel__item-badges">
            {item.generatedByModel ? <span className="mono">{item.generatedByModel}</span> : null}
            {item.generatedAt ? (
              <span className="mono">{new Date(item.generatedAt).toLocaleDateString()}</span>
            ) : null}
          </div>
        </div>
        {progressMessage ? (
          <div className="ev-brief-panel__call-progress">{progressMessage}</div>
        ) : null}
        {errorMessage ? <div className="ev-brief-panel__call-error">{errorMessage}</div> : null}
        <div className="ev-brief-panel__call-summary">{item.summary}</div>
        {item.objective ? (
          <div className="ev-brief-panel__call-objective">
            <span className="ev-brief-panel__mini-label">Objective</span>
            <span>{item.objective}</span>
          </div>
        ) : null}
        {item.topTalkingPoints.length > 0 ? (
          <div className="ev-brief-panel__call-list">
            {item.topTalkingPoints.slice(0, 3).map((point) => (
              <div key={point} className="ev-brief-panel__call-list-item">
                {point}
              </div>
            ))}
          </div>
        ) : null}
        <div className="ev-brief-panel__call-meta">
          {item.scheduledAt ? <span>{new Date(item.scheduledAt).toLocaleString()}</span> : null}
          {item.touchpointChannel ? <span>{item.touchpointChannel}</span> : null}
          <span>{item.missingInputs.length} missing inputs</span>
          {item.freshnessState ? <span>{item.freshnessState}</span> : null}
        </div>
      </button>

      <div className="ev-brief-panel__item-actions">
        <button
          type="button"
          className="ev-brief-panel__toggle"
          onClick={() => onToggleExpanded(itemKey)}
        >
          {expanded ? "Show less" : "View more"}
        </button>
      </div>

      {expanded ? (
        <div className="ev-brief-panel__item-expanded">
          <CallPrepStructuredView
            status={displayStatus}
            data={{
              summary: item.summary,
              objective: item.objective,
              whyNow: item.whyNow,
              relationshipSnapshot: item.relationshipSnapshot,
              priorCallHighlights: [],
              commercialState: null,
              teamInvestorContext: [],
              talkingPoints: item.topTalkingPoints,
              questionsToAsk: item.openQuestions,
              risksObjections: item.risks,
              missingInputs: item.missingInputs,
              desiredNextStep: item.desiredNextStep,
              generatedByModel: item.generatedByModel,
              generatedAt: liveStatus?.generatedAt ?? item.generatedAt,
              freshnessState: liveStatus?.freshnessState ?? item.freshnessState,
            }}
          />
        </div>
      ) : null}
    </div>
  );
}

function WarmLeadCard({
  item,
  onOpen,
}: {
  item: EvWorkstationProject;
  onOpen: () => void | Promise<void>;
}) {
  const matches = item.warmLead.matches.slice(0, 4);
  const remaining = item.warmLead.matches.length - matches.length;

  return (
    <div className="ev-brief-panel__item ev-brief-panel__item--warm-lead">
      <button type="button" className="ev-brief-panel__item-hitbox" onClick={() => void onOpen()}>
        <div className="ev-brief-panel__item-head ev-brief-panel__item-head--stacked">
          <div className="ev-brief-panel__item-title-wrap">
            <span>
              {renderProjectLabel(
                item.project.name ?? item.project.twitter_handle,
                item.project.twitter_handle
              )}
            </span>
            <span className="warm-intro-badge">Warm Lead</span>
          </div>
          <div className="ev-brief-panel__item-badges">
            <span className="mono">{item.warmLead.contactCount} contacts</span>
            <span className="mono">{item.warmLead.matchedFundNames.length} funds</span>
            {item.warmLead.untouched && (
              <span className="warm-lead-untouched-tag">Needs outreach</span>
            )}
          </div>
        </div>
        {matches.length > 0 && (
          <div className="warm-lead-contacts-grid">
            {matches.map((match) => {
              const owners = match.contact.directContactOwners ?? [];
              const isTg = match.contact.isTelegramGroup;
              return (
                <div
                  key={`${match.fundName}-${match.contact.name}`}
                  className="warm-lead-contact-entry"
                >
                  <span className="warm-lead-contact-fund">{match.fundName}</span>
                  <span className="warm-lead-contact-person">
                    {isTg ? (
                      <>
                        <span className="warm-lead-tg-tag">TG</span> {match.contact.name}
                      </>
                    ) : (
                      <>
                        {match.contact.name}
                        {match.contact.role ? (
                          <span className="warm-lead-contact-role"> · {match.contact.role}</span>
                        ) : null}
                      </>
                    )}
                  </span>
                  {owners.length > 0 && (
                    <span className="warm-lead-contact-owners">
                      {owners.map((o) => (
                        <span
                          key={o}
                          className={`warm-lead-owner${o === "Ev" ? " warm-lead-owner--ev" : ""}`}
                        >
                          {o}
                        </span>
                      ))}
                    </span>
                  )}
                </div>
              );
            })}
            {remaining > 0 && <div className="warm-lead-contact-more">+{remaining} more</div>}
          </div>
        )}
      </button>
    </div>
  );
}

function CommunicationSignalCard({
  item,
  onOpenProject,
}: {
  item: EvBriefPayload["signals"][number];
  onOpenProject: (
    projectId: string,
    options?: { detailView?: "full" | "call_prep" | "operating_summary" }
  ) => void | Promise<void>;
}) {
  const canOpenProject = Boolean(item.projectId);

  return (
    <div className="ev-brief-panel__item ev-brief-panel__item--intel">
      <div className="ev-brief-panel__item-hitbox">
        <div className="ev-brief-panel__item-head ev-brief-panel__item-head--stacked">
          <div className="ev-brief-panel__item-title-wrap">
            <span>{item.title}</span>
            <span className={`ev-brief-panel__severity ev-brief-panel__severity--${item.severity}`}>
              {item.severity}
            </span>
          </div>
          <div className="ev-brief-panel__item-badges">
            <span className="mono">{item.sourceKind.replaceAll("_", " ")}</span>
          </div>
        </div>
        <div className="ev-brief-panel__call-summary">{item.summary}</div>
        {item.reasons.length > 0 ? (
          <div className="ev-brief-panel__call-list">
            {item.reasons.slice(0, 3).map((reason) => (
              <div key={reason} className="ev-brief-panel__call-list-item">
                {reason}
              </div>
            ))}
          </div>
        ) : null}
        {item.nextAction ? (
          <div className="ev-brief-panel__call-objective">
            <span className="ev-brief-panel__mini-label">Recommended next step</span>
            <span>{item.nextAction}</span>
          </div>
        ) : null}
      </div>
      {canOpenProject ? (
        <div className="ev-brief-panel__item-actions">
          <button
            type="button"
            className="ev-brief-panel__toggle"
            onClick={() => void onOpenProject(item.projectId ?? "", { detailView: "full" })}
          >
            Open project
          </button>
        </div>
      ) : null}
    </div>
  );
}

function CommunicationMeetingCard({
  item,
  onOpenProject,
}: {
  item: EvBriefPayload["meetings"][number];
  onOpenProject: (
    projectId: string,
    options?: { detailView?: "full" | "call_prep" | "operating_summary" }
  ) => void | Promise<void>;
}) {
  const canOpenProject = Boolean(item.matchedProjectId);

  return (
    <div className="ev-brief-panel__item ev-brief-panel__item--intel">
      <div className="ev-brief-panel__item-hitbox">
        <div className="ev-brief-panel__item-head ev-brief-panel__item-head--stacked">
          <div className="ev-brief-panel__item-title-wrap">
            <span>{item.summary ?? "Untitled meeting"}</span>
          </div>
          <div className="ev-brief-panel__item-badges">
            <span className="mono">{formatBriefDateTime(item.startAt)}</span>
            {item.organizerEmail ? <span className="mono">{item.organizerEmail}</span> : null}
          </div>
        </div>
        {item.whyItMatters.length > 0 ? (
          <div className="ev-brief-panel__call-list">
            {item.whyItMatters.slice(0, 3).map((reason) => (
              <div key={reason} className="ev-brief-panel__call-list-item">
                {reason}
              </div>
            ))}
          </div>
        ) : null}
        {item.talkingPoints.length > 0 ? (
          <div className="ev-brief-panel__call-objective">
            <span className="ev-brief-panel__mini-label">Talking points</span>
            <span>{item.talkingPoints.slice(0, 2).join(" • ")}</span>
          </div>
        ) : null}
        <div className="ev-brief-panel__call-meta">
          <span>{item.attendeeEmails.length} attendees</span>
          {item.missingContext.length > 0 ? (
            <span>{item.missingContext.length} missing context items</span>
          ) : null}
        </div>
      </div>
      {canOpenProject ? (
        <div className="ev-brief-panel__item-actions">
          <button
            type="button"
            className="ev-brief-panel__toggle"
            onClick={() =>
              void onOpenProject(item.matchedProjectId ?? "", { detailView: "call_prep" })
            }
          >
            Open call prep
          </button>
        </div>
      ) : null}
    </div>
  );
}

function CommunicationEmailCard({
  item,
  onOpenProject,
}: {
  item: EvBriefPayload["emails"][number];
  onOpenProject: (
    projectId: string,
    options?: { detailView?: "full" | "call_prep" | "operating_summary" }
  ) => void | Promise<void>;
}) {
  const canOpenProject = Boolean(item.matchedProjectId);

  return (
    <div className="ev-brief-panel__item ev-brief-panel__item--intel">
      <div className="ev-brief-panel__item-hitbox">
        <div className="ev-brief-panel__item-head ev-brief-panel__item-head--stacked">
          <div className="ev-brief-panel__item-title-wrap">
            <span>{item.subject ?? "No subject"}</span>
          </div>
          <div className="ev-brief-panel__item-badges">
            {item.fromName || item.fromEmail ? (
              <span className="mono">{item.fromName ?? item.fromEmail}</span>
            ) : null}
            <span className="mono">{formatBriefDateTime(item.receivedAt)}</span>
          </div>
        </div>
        {item.snippet ? <div className="ev-brief-panel__call-summary">{item.snippet}</div> : null}
        {item.whyItMatters.length > 0 ? (
          <div className="ev-brief-panel__call-list">
            {item.whyItMatters.slice(0, 2).map((reason) => (
              <div key={reason} className="ev-brief-panel__call-list-item">
                {reason}
              </div>
            ))}
          </div>
        ) : null}
        {item.recommendedAction ? (
          <div className="ev-brief-panel__call-objective">
            <span className="ev-brief-panel__mini-label">Recommended reply</span>
            <span>{item.recommendedAction}</span>
          </div>
        ) : null}
      </div>
      {canOpenProject ? (
        <div className="ev-brief-panel__item-actions">
          <button
            type="button"
            className="ev-brief-panel__toggle"
            onClick={() =>
              void onOpenProject(item.matchedProjectId ?? "", { detailView: "operating_summary" })
            }
          >
            Open project
          </button>
        </div>
      ) : null}
    </div>
  );
}

function CommunicationActionCard({
  item,
  onOpenProject,
}: {
  item: EvBriefPayload["recommendedActions"][number];
  onOpenProject: (
    projectId: string,
    options?: { detailView?: "full" | "call_prep" | "operating_summary" }
  ) => void | Promise<void>;
}) {
  return (
    <div className="ev-brief-panel__item ev-brief-panel__item--intel">
      <div className="ev-brief-panel__item-hitbox">
        <div className="ev-brief-panel__item-head ev-brief-panel__item-head--stacked">
          <div className="ev-brief-panel__item-title-wrap">
            <span>{item.label}</span>
          </div>
          {item.dueAt ? (
            <div className="ev-brief-panel__item-badges">
              <span className="mono">{formatBriefDateTime(item.dueAt)}</span>
            </div>
          ) : null}
        </div>
        <div className="ev-brief-panel__call-summary">{item.reason}</div>
      </div>
      {item.projectId ? (
        <div className="ev-brief-panel__item-actions">
          <button
            type="button"
            className="ev-brief-panel__toggle"
            onClick={() => void onOpenProject(item.projectId ?? "", { detailView: "full" })}
          >
            Open project
          </button>
        </div>
      ) : null}
    </div>
  );
}

export function EvDailyBriefPanel({
  brief,
  loading,
  onOpenProject,
  onApplyPriorityAction,
  priorityActionPendingKey,
}: EvDailyBriefPanelProps) {
  const [expandedItems, setExpandedItems] = useState<Record<string, boolean>>({});
  const [priorityActionModal, setPriorityActionModal] = useState<{
    item: EvBriefPayload["priorities"][number];
    action: EvPriorityActionType;
  } | null>(null);

  function toggleExpanded(key: string) {
    setExpandedItems((current) => ({ ...current, [key]: !current[key] }));
  }

  return (
    <section className="ev-brief-panel">
      <div className="ev-brief-panel__header">
        <div>
          <span className="section-title">Today&apos;s Brief</span>
          {brief?.generatedByModel && (
            <div className="ev-brief-panel__subhead">Generated via {brief.generatedByModel}</div>
          )}
        </div>
        <span className="section-count">
          {brief
            ? `${(brief.priorities ?? []).length} priorities • ${(brief.warmLeadsNeedingOutreach ?? []).length} warm leads • ${(brief.calls ?? []).length} calls • ${(brief.signals ?? []).length + (brief.meetings ?? []).length + (brief.emails ?? []).length + (brief.recommendedActions ?? []).length} comm items`
            : "—"}
        </span>
      </div>

      {loading ? (
        <div className="loading">
          <div className="spinner" />
          <span className="loading-text">Building daily brief</span>
        </div>
      ) : !brief ? (
        <div className="empty-state">
          <h3>Daily brief unavailable</h3>
          <p>The workstation brief has not been generated yet.</p>
        </div>
      ) : (
        <div className="ev-brief-panel__grid">
          <div className="ev-brief-panel__column ev-brief-panel__column--priorities">
            <div className="ev-brief-panel__column-head">
              <div className="ev-brief-panel__column-title">Priorities</div>
              <span className="ev-brief-panel__count-pill">{(brief.priorities ?? []).length}</span>
            </div>
            <div className="ev-brief-panel__list">
              {(brief.priorities ?? []).length === 0 ? (
                <div className="community-empty">No urgent priorities right now.</div>
              ) : (
                (brief.priorities ?? []).map((item) => (
                  <PriorityCard
                    key={item.priorityKey}
                    item={item}
                    expanded={expandedItems[`priority-${item.priorityKey}`] === true}
                    onToggleExpanded={toggleExpanded}
                    onOpen={() =>
                      onOpenProject(item.projectId, { detailView: "operating_summary" })
                    }
                    onSelectAction={(priorityItem, action) => {
                      setPriorityActionModal({ item: priorityItem, action });
                    }}
                    actionPendingKey={priorityActionPendingKey}
                  />
                ))
              )}
            </div>
          </div>

          <div className="ev-brief-panel__column ev-brief-panel__column--warm-leads">
            <div className="ev-brief-panel__column-head">
              <div className="ev-brief-panel__column-title">Warm Leads</div>
              <span className="ev-brief-panel__count-pill">
                {(brief.warmLeadsNeedingOutreach ?? []).length}
              </span>
            </div>
            <div className="ev-brief-panel__list">
              {(brief.warmLeadsNeedingOutreach ?? []).length === 0 ? (
                <div className="community-empty">No untouched warm leads right now.</div>
              ) : (
                (brief.warmLeadsNeedingOutreach ?? []).map((item) => (
                  <WarmLeadCard
                    key={item.project.id}
                    item={item}
                    onOpen={() => onOpenProject(item.project.id)}
                  />
                ))
              )}
            </div>
          </div>

          <div className="ev-brief-panel__column ev-brief-panel__column--calls">
            <div className="ev-brief-panel__column-head">
              <div className="ev-brief-panel__column-title">Ready for Call</div>
              <span className="ev-brief-panel__count-pill">{(brief.calls ?? []).length}</span>
            </div>
            <div className="ev-brief-panel__list">
              {(brief.calls ?? []).length === 0 ? (
                <div className="community-empty">No confirmed calls are surfaced for today.</div>
              ) : (
                (brief.calls ?? []).map((item) => (
                  <CallPrepCard
                    key={item.projectId}
                    item={item}
                    expanded={expandedItems[`call-${item.projectId}`] === true}
                    onToggleExpanded={toggleExpanded}
                    onOpen={() => onOpenProject(item.projectId, { detailView: "call_prep" })}
                  />
                ))
              )}
            </div>
          </div>

          <div className="ev-brief-panel__column ev-brief-panel__column--communications">
            <div className="ev-brief-panel__column-head">
              <div className="ev-brief-panel__column-title">Communication Intel</div>
              <span className="ev-brief-panel__count-pill">
                {(brief.signals ?? []).length +
                  (brief.recommendedActions ?? []).length +
                  (brief.meetings ?? []).length +
                  (brief.emails ?? []).length}
              </span>
            </div>
            <div className="ev-brief-panel__list">
              {(brief.signals ?? []).length === 0 &&
              (brief.recommendedActions ?? []).length === 0 &&
              (brief.meetings ?? []).length === 0 &&
              (brief.emails ?? []).length === 0 ? (
                <div className="community-empty">No email or calendar intelligence yet.</div>
              ) : (
                <>
                  {(brief.signals ?? []).slice(0, 3).map((item) => (
                    <CommunicationSignalCard
                      key={item.id}
                      item={item}
                      onOpenProject={onOpenProject}
                    />
                  ))}
                  {(brief.recommendedActions ?? []).slice(0, 2).map((item) => (
                    <CommunicationActionCard
                      key={`${item.label}-${item.projectId ?? "global"}`}
                      item={item}
                      onOpenProject={onOpenProject}
                    />
                  ))}
                  {(brief.meetings ?? []).slice(0, 2).map((item) => (
                    <CommunicationMeetingCard
                      key={item.eventId}
                      item={item}
                      onOpenProject={onOpenProject}
                    />
                  ))}
                  {(brief.emails ?? []).slice(0, 2).map((item) => (
                    <CommunicationEmailCard
                      key={item.messageId}
                      item={item}
                      onOpenProject={onOpenProject}
                    />
                  ))}
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {priorityActionModal ? (
        <PriorityActionModal
          item={priorityActionModal.item}
          action={priorityActionModal.action}
          pending={priorityActionPendingKey === priorityActionModal.item.priorityKey}
          onClose={() => setPriorityActionModal(null)}
          onSubmit={async (input) => {
            await onApplyPriorityAction(priorityActionModal.item.projectId, input);
          }}
        />
      ) : null}
    </section>
  );
}
